import SelectGroup from "../../../../components/Forms/SelectGroup/SelectGroup.tsx";
import SectionHeader from "../../../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../../../constants/formStyles.ts";
import { insurerName } from "../../../../constants/insurers.ts";
import type { LifecycleController } from "../useLifecycle.ts";
import { BlankPicker } from "./BlankPicker.tsx";
import { CARD, InfoRow, Spinner } from "./shared.tsx";

export function IssuePanel({
  controller,
}: {
  controller: LifecycleController;
}) {
  const {
    quote,
    selected,
    provider,
    booking,
    missingRequired,
    canIssue,
    offerSigned,
    setOfferSigned,
    busy,
    issue,
    bookingForm,
  } = controller;
  const {
    cid,
    companies,
    companiesLoading,
    divisionId,
    agentId,
    commission,
    additionalData,
    offices,
    agents,
    setCid,
    setDivisionId,
    setAgentId,
    setCommission,
    setAdditionalData,
  } = bookingForm;
  return (
    <>
      {quote && selected?.canIssue && selected.premium && (
        <fieldset disabled={controller.busy !== null} className={CARD}>
          <SectionHeader title="Издаване" />
          <div className="p-4 pt-2">
            <div className="mb-4 grid grid-cols-1 gap-3 border-b border-stroke pb-4 dark:border-strokedark sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <div className={labelClass}>Избран застраховател</div>
                <div
                  className={`${inputClass} min-h-[42px] bg-gray-2 dark:bg-meta-4`}
                >
                  {insurerName(provider)}
                </div>
              </div>
              <div>
                <label htmlFor="IssuePanel-company" className={labelClass}>
                  Агенция на застрахователя
                </label>
                <SelectGroup
                  id="IssuePanel-company"
                  value={cid}
                  onChange={(value) => setCid(String(value))}
                  options={companies}
                  disabled={companiesLoading}
                  placeholder={
                    companiesLoading ? "Зареждане..." : "— изберете —"
                  }
                />
              </div>
              <div>
                <label htmlFor={"IssuePanel-1"} className={labelClass}>
                  Офис
                </label>
                <SelectGroup
                  id={"IssuePanel-1"}
                  value={divisionId}
                  onChange={(value) => setDivisionId(String(value))}
                  options={offices}
                  placeholder="— изберете —"
                />
              </div>
              <div>
                <label htmlFor={"IssuePanel-2"} className={labelClass}>
                  Агент
                </label>
                <SelectGroup
                  id={"IssuePanel-2"}
                  value={agentId}
                  onChange={(value) => setAgentId(String(value))}
                  options={agents}
                  placeholder="— изберете —"
                />
              </div>
              <div>
                <label htmlFor={"IssuePanel-3"} className={labelClass}>
                  Комисиона (%)
                </label>
                <input
                  id={"IssuePanel-3"}
                  className={inputClass}
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={commission}
                  onChange={(e) => setCommission(e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor={"IssuePanel-5"} className={labelClass}>
                  Допълнителни данни
                </label>
                <input
                  id={"IssuePanel-5"}
                  className={inputClass}
                  value={additionalData}
                  onChange={(e) => setAdditionalData(e.target.value)}
                />
              </div>
            </div>

            {booking && (
              <div className="mb-4 rounded-sm bg-gray-2 p-3 dark:bg-meta-4">
                <p className="mb-2 text-xs font-semibold text-black dark:text-white">
                  Разбивка на премията от избраната оферта
                </p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  <InfoRow
                    label="Застрахователна сума"
                    value={booking.insuredAmount.amount.toFixed(2)}
                  />
                  <InfoRow
                    label="Основна премия"
                    value={booking.basePremium.amount.toFixed(2)}
                  />
                  <InfoRow
                    label="Гаранционен фонд"
                    value={booking.guaranteeFund.amount.toFixed(2)}
                  />
                  <InfoRow
                    label="Данък върху премията"
                    value={booking.tax.amount.toFixed(2)}
                  />
                  <InfoRow
                    label="Стикер и други такси"
                    value={(
                      booking.stickerFee.amount + booking.otherFees.amount
                    ).toFixed(2)}
                  />
                  <InfoRow
                    label="Брой вноски"
                    value={booking.installmentCount}
                  />
                  <InfoRow
                    label="Общо дължима премия"
                    value={`${booking.total.amount.toFixed(2)} ${
                      booking.currency
                    }`}
                  />
                </div>
                <p className="mt-2 text-[11px] text-body dark:text-bodydark">
                  {booking.coverageStart} — {booking.coverageEnd}
                </p>
              </div>
            )}

            <BlankPicker form={controller.blankForm} />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <div className="text-[11px]">
                {!controller.records.ready && (
                  <p className="text-warning">
                    Проверете и запишете данните за застраховащия и автомобила
                    преди издаване.
                  </p>
                )}
                {missingRequired.length > 0 && (
                  <p className="text-[#AA2808]">
                    Регистърът не върна:{" "}
                    {missingRequired.map((m) => m.label).join(", ")} - попълнете
                    от талона, за да издадете.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-black dark:text-white">
                  <input
                    type="checkbox"
                    checked={offerSigned}
                    onChange={(event) => setOfferSigned(event.target.checked)}
                    disabled={controller.stale}
                    className="h-4 w-4 accent-primary"
                  />
                  Офертата е подписана
                </label>
                <button
                  onClick={issue}
                  disabled={!canIssue}
                  className="flex shrink-0 items-center gap-1.5 rounded bg-primary px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy === "issue" ? <Spinner /> : null}
                  {busy === "issue"
                    ? "Издаване..."
                    : `Издай — ${selected.premium.eur.toFixed(2)} EUR`}
                </button>
              </div>
            </div>
          </div>
        </fieldset>
      )}
    </>
  );
}
